import * as React from 'react';
import { Column } from 'react-table';
import { IFileForTable } from '../Files/FileContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  DomainContext,
  FILE_SUB_TYPE_COMMUNITY_IMPACT,
  FILE_SUB_TYPE_GLADUE_REPORT,
  FILE_SUB_TYPE_INTAKE_REQUEST_FILE,
  FILE_SUB_TYPE_LEGAL_SYSTEM_NAVIGATOR,
  FILE_SUB_TYPE_WCC_CONNECTIVE,
  FILE_TYPE_JUSTICE,
  IFileSubType,
  INDIGENOUS_GROUP_FIRST_NATION,
  INDIGENOUS_GROUP_INUIT,
  INDIGENOUS_GROUP_METIS,
  IStage,
  STAGE_INTAKE_IN_PROGRESS,
  STAGE_REJECTED,
} from '../../contexts/DomainContext';
import { IClient } from '../Clients/ClientContext';
import { CLIENTS_STATE_LOADED, ClientsContext } from '../../contexts/ClientsContext';
import { DropdownMultiSelectCheckboxes } from '../DropdownMultiSelectCheckboxes/DropdownMultiSelectCheckboxes';
import { IUser } from '../Users/UsersTable';
import { UsersContext } from '../../contexts/UsersContext';
import { formatDateFromISONumber } from '../../formatters/DateTimeFormatter';
import { DateRangeDropdown } from '../DateRangeDropdown/DateRangeDropdown';
import { JusticeStageDropdownMultiSelectCheckboxes } from '../DropdownMultiSelectCheckboxes/JusticeStageDropdownMultiSelectCheckboxes';
import { FaLock } from 'react-icons/fa';

export const useJusticeColumns = (justiceStaff: string[]): Column<IFileForTable>[] => {
  const domainContext = useContext(DomainContext);
  const usersContext = useContext(UsersContext);
  const stages = domainContext.stages;
  const fileTypes = domainContext.fileTypes;
  const fileSubTypes = domainContext.fileSubTypes;
  const justiceFileSubTypes = fileSubTypes.filter((f) => f.fileTypeId === FILE_TYPE_JUSTICE);
  const clientsContext = useContext(ClientsContext);
  const clients = clientsContext.state.clients;
  const staff = usersContext.users;
  const yukonFns = domainContext.yukonFns;
  //filtered list of user objects
  const [filteredStaff, setFilteredStaff] = useState<IUser[]>(staff.filter((s) => justiceStaff.includes(s.userId)));

  useEffect(() => {
    setFilteredStaff(staff.filter((s) => justiceStaff.includes(s.userId)));
  }, [staff, justiceStaff]);

  return useMemo(
    () => [
      {
        Header: 'File #',
        accessor: 'fileNumber',
        Cell: (cell: any) => {
          return cell.row.original.isConfidential ? (
            <div className={'d-flex align-items-center'}>
              {cell.row.original.fileNumber} <FaLock className={'ml-2'} color={'#912f1d'} />
            </div>
          ) : (
            cell.row.original.fileNumber
          );
        },
        width: 125,
        xs: true,
      },
      {
        Header: 'Client',
        id: 'clientIds',
        width: 250,
        xs: true,
        accessor: (file: IFileForTable) => {
          if (file.fileDisplayName) {
            return file.fileDisplayName;
          } else {
            const clientsMapped = file.clientIds.map((pId) => {
              const client = clients.find((i: IClient) => i.clientId === pId);
              if (!client && clientsContext.state.type === CLIENTS_STATE_LOADED) {
                clientsContext.dispatch({ type: 'CLIENTS_ACTION_DO_RELOADED' });
              }
              return client;
            });
            return clientsMapped.length > 0
              ? clientsMapped.map((p) => p?.lastName.toUpperCase() + ', ' + p?.firstName).join(' • ')
              : '';
          }
        },
        filter: (rows: any, id: any, filterValue: any) => {
          return rows.filter((row: any) => {
            if (filterValue.length === 0) {
              return true;
            } else {
              const clientsMapped: (IClient | undefined)[] = row.original['clientIds'].map((cId: string) => {
                const client = clients.find((i: IClient) => i.clientId === cId);
                if (!client && clientsContext.state.type === CLIENTS_STATE_LOADED) {
                  clientsContext.dispatch({ type: 'CLIENTS_ACTION_DO_RELOADED' });
                }
                return client;
              });
              const clientsNamesFormatted =
                clientsMapped.length > 0
                  ? clientsMapped.map((p) => p?.lastName.toUpperCase() + ', ' + p?.firstName.toUpperCase()).join(' • ')
                  : '';
              const clientsNamesUnformatted =
                clientsMapped.length > 0
                  ? clientsMapped.map((p) => p?.firstName.toUpperCase() + ' ' + p?.lastName.toUpperCase()).join(' • ')
                  : '';
              return (
                clientsNamesFormatted.includes(filterValue?.toString()?.toUpperCase() ?? '') ||
                clientsNamesUnformatted.includes(filterValue?.toString()?.toUpperCase() ?? '')
              );
            }
          });
        },
      },
      {
        Header: 'Stage',
        id: 'stageIdTuple',
        width: 150,
        xs: true,
        accessor: (file: IFileForTable) => {
          return stages.find((s: IStage) => s.stageId === file.stageId)?.name;
        },
        sortType: (rowA: any, rowB: any, id: any, desc: any) => {
          const rowAStage = stages.find((s: IStage) => s.stageId === rowA.original[id]);
          const rowBStage = stages.find((s: IStage) => s.stageId === rowB.original[id]);
          if (rowAStage?.order && rowBStage?.order) {
            if (rowAStage.order > rowBStage.order) return 1;
            if (rowBStage.order > rowAStage.order) return -1;
            if (rowAStage.order === rowBStage?.order) {
              if (rowAStage.name > rowBStage?.name) {
                return 1;
              } else if (rowAStage.name < rowBStage.name) {
                return -1;
              } else {
                return 0;
              }
            }
          }
          return 0;
        },
        // eslint-disable-next-line react/display-name
        Filter: ({
          column: {
            filterValue = stages.map((s) => s.fileSubTypes.map((st) => [s.stageId, st])),
            preFilteredRows,
            setFilter,
            id,
          },
        }) => {
          return (
            <JusticeStageDropdownMultiSelectCheckboxes
              values={filterValue}
              keyPrefix='stageId'
              setValues={(val: [string, string][]) => {
                setFilter(val);
              }}
              options={stages
                .filter(
                  (s) =>
                    s.fileTypes.includes(FILE_TYPE_JUSTICE) ||
                    s.fileSubTypes.includes(FILE_SUB_TYPE_INTAKE_REQUEST_FILE)
                )
                .map((s: IStage) => ({
                  subType: s.fileSubTypes,
                  value: s.stageId,
                  display: s.name,
                }))}
            />
          );
        },
        filter: (rows: any, id: any, filterValue: [string, string][]) => {
          return rows.filter((row: any) => {
            if (filterValue.length === 0) {
              return true;
            } else {
              const rowStageId = row.original['stageId'];
              const rowFileSubType = row.original['fileSubTypeId'];
              if (rowStageId === STAGE_INTAKE_IN_PROGRESS || rowStageId === STAGE_REJECTED) {
                return filterValue.find((val: any) => rowStageId === val[1]);
              } else {
                return filterValue.find((val: any) => rowFileSubType === val[0] && rowStageId === val[1]);
              }
            }
          });
        },
      },
      {
        Header: 'File Type',
        id: 'fileSubTypeId',
        accessor: (file: IFileForTable) => {
          return fileSubTypes.find((f) => f.fileSubTypeId === file.fileSubTypeId)?.name;
        },
        width: 250,
        xs: true,
        // eslint-disable-next-line react/display-name
        Filter: ({
          column: { filterValue = justiceFileSubTypes.map((f) => f.fileSubTypeId), preFilteredRows, setFilter, id },
        }) => {
          return (
            <DropdownMultiSelectCheckboxes
              values={filterValue}
              keyPrefix='fileSubTypeId'
              setValues={(val: string[]) => {
                setFilter(val);
              }}
              options={justiceFileSubTypes.map((f: IFileSubType) => ({
                value: f.fileSubTypeId,
                display: f.name,
              }))}
            />
          );
        },
        filter: (rows: any, id: any, filterValue: string[]) => {
          return rows.filter((row: any) => {
            if (filterValue.length === 0) {
              return true;
            } else {
              const rowValue = row.original['fileSubTypeId'];
              return filterValue.find((val: any) => rowValue === val);
            }
          });
        },
      },
      {
        Header: 'Staff',
        id: 'staffIds',
        width: undefined,
        xs: true,
        accessor: (file: IFileForTable) => {
          return filteredStaff
            .sort((a, b) =>
              (a.lastName + a.firstName).toUpperCase().localeCompare((b.lastName + b.firstName).toUpperCase())
            )
            .filter((s: IUser) => file.staffIds.includes(s.userId))
            .map((s) => s.lastName.toUpperCase() + ', ' + s.firstName + (s.active ? '' : ' (Inactive)'))
            .join(' • ');
        },
        // eslint-disable-next-line react/display-name
        Filter: ({ column: { filterValue = filteredStaff.map((s) => s.userId), preFilteredRows, setFilter, id } }) => {
          return (
            <DropdownMultiSelectCheckboxes
              values={filterValue}
              keyPrefix='userId'
              setValues={(val: string[]) => {
                setFilter(val);
              }}
              options={filteredStaff.map((s: IUser) => ({
                value: s.userId,
                display: s.lastName + ', ' + s.firstName,
              }))}
            />
          );
        },
        filter: (rows: any, id: any, filterValue: string[]) => {
          return rows.filter((row: any) => {
            if (filterValue.length === 0) {
              return true;
            } else {
              const rowValue = row.original['staffIds'];
              return filterValue.find((val: any) => rowValue.includes(val));
            }
          });
        },
      },
      {
        Header: 'First Nation',
        id: 'firstNation',
        width: 300,
        xs: true,
        accessor: (file: IFileForTable) => {
          if (file.fileSubTypeId === FILE_SUB_TYPE_COMMUNITY_IMPACT) {
            return yukonFns.find((fn) => fn.yukonFirstNationId === file.communityImpactFields?.firstNation)
              ?.abbreviation;
          } else if (file.fileSubTypeId === FILE_SUB_TYPE_WCC_CONNECTIVE) {
            if (file.wccConnectiveFields?.indigenousGroupId === INDIGENOUS_GROUP_FIRST_NATION) {
              return yukonFns.find((fn) => fn.yukonFirstNationId === file.wccConnectiveFields?.bandNation)
                ?.abbreviation;
            } else if (
              file.wccConnectiveFields?.indigenousGroupId === INDIGENOUS_GROUP_METIS ||
              file.wccConnectiveFields?.indigenousGroupId === INDIGENOUS_GROUP_INUIT
            ) {
              return file.wccConnectiveFields?.bandNation;
            }
          } else if (file.fileSubTypeId === FILE_SUB_TYPE_GLADUE_REPORT) {
            if (file.gladueReportFields?.indigenousGroupId === INDIGENOUS_GROUP_FIRST_NATION) {
              return yukonFns.find((fn) => fn.yukonFirstNationId === file.gladueReportFields?.bandNation)?.abbreviation;
            } else if (
              file.gladueReportFields?.indigenousGroupId === INDIGENOUS_GROUP_METIS ||
              file.gladueReportFields?.indigenousGroupId === INDIGENOUS_GROUP_INUIT
            ) {
              return file.gladueReportFields?.bandNation;
            }
          } else if (file.fileSubTypeId === FILE_SUB_TYPE_LEGAL_SYSTEM_NAVIGATOR) {
            if (file.legalSystemNavigatorFields?.indigenousGroupId === INDIGENOUS_GROUP_FIRST_NATION) {
              return yukonFns.find((fn) => fn.yukonFirstNationId === file.legalSystemNavigatorFields?.bandNation)
                ?.abbreviation;
            } else if (
              file.legalSystemNavigatorFields?.indigenousGroupId === INDIGENOUS_GROUP_METIS ||
              file.legalSystemNavigatorFields?.indigenousGroupId === INDIGENOUS_GROUP_INUIT
            ) {
              return file.legalSystemNavigatorFields?.bandNation;
            }
          }
          // return '';
        },
      },
      {
        Header: 'Sentencing Date',
        id: 'sentencingDate',
        accessor: (file: IFileForTable) => {
          if (file.fileTypeId === FILE_TYPE_JUSTICE) {
            if (file.fileSubTypeId === FILE_SUB_TYPE_GLADUE_REPORT) {
              return file.gladueReportFields?.sentencingDate;
            } else if (file.fileSubTypeId === FILE_SUB_TYPE_WCC_CONNECTIVE) {
              return file.wccConnectiveFields?.sentencingDate;
            } else if (file.fileSubTypeId === FILE_SUB_TYPE_COMMUNITY_IMPACT) {
              return file.communityImpactFields?.sentencingDate;
            } else {
              return undefined;
            }
          } else {
            return undefined;
          }
        },
        width: 175,
        xs: true,
        Cell: (c: any) => {
          return c.row.values.sentencingDate ? formatDateFromISONumber(c.row.values.sentencingDate) : '';
        },
        filter: (rows: any, id: any, filterValue: { startDate?: Date; endDate?: Date }) => {
          return rows.filter((row: any) => {
            if (!filterValue.startDate || !filterValue.endDate) {
              return true;
            } else {
              if (row.original.fileTypeId === FILE_TYPE_JUSTICE) {
                if (row.original.fileSubTypeId === FILE_SUB_TYPE_GLADUE_REPORT && row.original.gladueReportFields) {
                  const rowValue = row.original.gladueReportFields['sentencingDate'];
                  if (rowValue) {
                    return rowValue >= filterValue.startDate.getTime() && rowValue <= filterValue.endDate.getTime();
                  }
                }
                if (
                  row.original.fileSubTypeId === FILE_SUB_TYPE_COMMUNITY_IMPACT &&
                  row.original.communityImpactFields
                ) {
                  const rowValue = row.original.communityImpactFields['sentencingDate'];
                  if (rowValue) {
                    return rowValue >= filterValue.startDate.getTime() && rowValue <= filterValue.endDate.getTime();
                  }
                }
                if (row.original.fileSubTypeId === FILE_SUB_TYPE_WCC_CONNECTIVE && row.original.wccConnectiveFields) {
                  const rowValue = row.original.wccConnectiveFields['sentencingDate'];
                  if (rowValue) {
                    return rowValue >= filterValue.startDate.getTime() && rowValue <= filterValue.endDate.getTime();
                  }
                }
              }
            }
          });
        },
        // eslint-disable-next-line react/display-name
        Filter: ({ column: { filterValue, preFilteredRows, setFilter, id } }) => (
          <DateRangeDropdown
            startDate={filterValue?.startDate}
            endDate={filterValue?.endDate}
            setRange={(dates: { startDate?: Date; endDate?: Date }) => {
              setFilter(dates);
            }}
            keyPrefix={'sentencingDate'}
          />
        ),
      },
      {
        Header: 'Requested Date',
        id: 'requestedDate',
        accessor: (file: IFileForTable) => {
          if (file.fileTypeId === FILE_TYPE_JUSTICE) {
            if (file.fileSubTypeId === FILE_SUB_TYPE_GLADUE_REPORT) {
              return file.gladueReportFields?.requestedDate;
            } else if (
              file.fileSubTypeId === FILE_SUB_TYPE_WCC_CONNECTIVE ||
              file.fileSubTypeId === FILE_SUB_TYPE_COMMUNITY_IMPACT ||
              file.fileSubTypeId === FILE_SUB_TYPE_LEGAL_SYSTEM_NAVIGATOR
            ) {
              return file.createdDate;
            } else {
              return undefined;
            }
          } else {
            return undefined;
          }
        },
        width: 175,
        xs: true,
        Cell: (c: any) =>
          c.row.original.gladueReportFields
            ? formatDateFromISONumber(c.row.original.gladueReportFields.requestedDate)
            : c.row.original.createdDate
            ? formatDateFromISONumber(c.row.original.createdDate)
            : '',
        filter: (rows: any, id: any, filterValue: { startDate?: Date; endDate?: Date }) => {
          return rows.filter((row: any) => {
            if (!filterValue.startDate || !filterValue.endDate) {
              return true;
            } else {
              if (row.original.gladueReportFields) {
                const rowValue = row.original.gladueReportFields['requestedDate'];
                return rowValue > filterValue.startDate.getTime() && rowValue < filterValue.endDate.getTime();
              } else {
                const rowValue = row.original['createdDate'];
                return rowValue > filterValue.startDate.getTime() && rowValue < filterValue.endDate.getTime();
              }
            }
          });
        },
        // eslint-disable-next-line react/display-name
        Filter: ({ column: { filterValue, preFilteredRows, setFilter, id } }) => (
          <DateRangeDropdown
            startDate={filterValue?.startDate}
            endDate={filterValue?.endDate}
            setRange={(dates: { startDate?: Date; endDate?: Date }) => {
              setFilter(dates);
            }}
            keyPrefix={'createdDate'}
          />
        ),
      },
    ],
    [stages, fileTypes, filteredStaff]
  );
};
